import React from 'react'
// libs
import { isEmpty } from 'lodash/fp'
// constants
import { TREE_SCROLL_CONTAINER_ID } from './constants'
// utils
import { buildParentMap, collectExpandedIds, defaultGetNodeTitle, findNode, getProjection, insertUnderParent, isDescendant, removeNode } from './utils'
// types
import {
  IDndState,
  IDragOver,
  IProjection,
  IRowProps,
  ITableTreeColumn,
  ITableTreeNode,
  TDropPosition,
  TRenderNode
} from './types'
// views
import {
  ActionsHeadCell,
  CellContent,
  DropLine,
  HeadCell,
  PlaceholderWrapper,
  RootDropZone,
  Table,
  TreeWrapper
} from './views'

export type { ITableTreeNode, ITableTreeColumn, IRowProps } from './types'

interface ITableTreeProps {
  treeData: ITableTreeNode[]
  isLoading?: boolean
  placeholder?: React.ReactNode
  getNodeTitle?: (node: ITableTreeNode) => string
  // Optional render-prop for the trailing actions column (kept plan-agnostic here).
  renderRowActions?: TRenderNode
  // Optional render-prop for helper/create-form nodes (rendered as a full-width indented row).
  renderCreateForm?: TRenderNode
  // Optional content rendered below the table (e.g. a root create-item control).
  footer?: React.ReactNode
  // Fired after a node is expanded/collapsed, so the host can persist the `expanded` flag.
  onToggleExpand?: (node: ITableTreeNode, expanded: boolean) => void
  // Fired on row click (excluding the collapse toggle and the actions column).
  onRowClick?: (node: ITableTreeNode) => void
  // Whether a node may be dragged. When omitted, drag & drop is disabled entirely (needs onMoveNode too).
  canDrag?: (node: ITableTreeNode) => boolean
  // Whether the dragged node may land under `nextParent` (null = root level).
  canDrop?: (dragNode: ITableTreeNode, nextParent: ITableTreeNode | null) => boolean
  // Fired after a successful move with the reordered nested tree, so the host can persist positions.
  onMoveNode?: (nextTree: ITableTreeNode[]) => void
  // Dims rows that don't match the active search/filter (returns true → dimmed).
  isRowDimmed?: (node: ITableTreeNode) => boolean
  // Render all rows expanded (e.g. while searching), without touching the user's collapse state.
  forceExpandAll?: boolean
  // Generic data columns rendered between the tree column and the trailing actions column.
  columns?: ITableTreeColumn[]
  // Header of the trailing actions column (shown only with renderRowActions).
  actionsHeader?: React.ReactNode
  // Row renderer (required) — the host owns row markup; applied recursively to every row.
  rowComponent: React.ComponentType<IRowProps>
}

const TableTree: React.FC<ITableTreeProps> = ({
  treeData,
  isLoading,
  placeholder,
  getNodeTitle = defaultGetNodeTitle,
  renderRowActions,
  renderCreateForm,
  footer,
  onToggleExpand,
  onRowClick,
  canDrag,
  canDrop,
  onMoveNode,
  isRowDimmed,
  forceExpandAll,
  columns = [],
  actionsHeader,
  rowComponent: RowComponent
}) => {
  const [expanded, setExpanded] = React.useState<Set<string | number>>(() => collectExpandedIds(treeData || []))
  // Working copy of the tree so drag & drop can reorder optimistically before the host persists.
  const [tree, setTree] = React.useState<ITableTreeNode[]>(treeData || [])
  const [draggedId, setDraggedId] = React.useState<string | number | null>(null)
  const [dragOver, setDragOver] = React.useState<IDragOver>({ id: null, position: null, depth: 0 })
  // Highlight state for the trailing "move to top level" drop zone.
  const [rootDropActive, setRootDropActive] = React.useState(false)
  // Visible width of the scroll container, so inline forms match the viewport (not the full wide table).

  const [viewportWidth, setViewportWidth] = React.useState(0)

  const wrapperRef = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    const el = wrapperRef.current

    if (!el) {
      return
    }

    const update = () => setViewportWidth(el.clientWidth)

    update()

    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Horizontal position where the drag began — used to project the target nesting depth from the pointer's X.
  const dragStartXRef = React.useRef(0)
  // Latest projection (parent/anchor) computed during drag-over, applied on drop.
  const projectionRef = React.useRef<IProjection | null>(null)

  React.useEffect(() => {
    setExpanded(collectExpandedIds(treeData || []))
    setTree(treeData || [])
  }, [treeData])

  const toggleExpand = (node: ITableTreeNode): void => {
    const willExpand = !expanded.has(node.id)

    setExpanded((prev) => {
      const next = new Set(prev)
      if (willExpand) {
        next.add(node.id)
      } else {
        next.delete(node.id)
      }
      return next
    })

    onToggleExpand?.(node, willExpand)
  }

  // Drag & drop is enabled only when the host both allows dragging and wants to persist moves.
  const isDndEnabled = !!onMoveNode && !!canDrag

  const handleDragStart = (event: React.DragEvent, node: ITableTreeNode): void => {
    setDraggedId(node.id)
    dragStartXRef.current = event.clientX
    event.dataTransfer.effectAllowed = 'move'
    // Firefox requires drag data to be set for the drag to actually start.
    event.dataTransfer.setData('text/plain', String(node.id))
  }

  const handleDragOver = (event: React.DragEvent, node: ITableTreeNode): void => {
    event.preventDefault()
    if (draggedId === null || draggedId === node.id) {
      return
    }

    // A node can never land inside its own subtree.
    const activeNode = findNode(tree, draggedId)
    if (activeNode && isDescendant(activeNode, node.id)) {
      return
    }

    const rect = event.currentTarget.getBoundingClientRect()
    const position: TDropPosition = (event.clientY - rect.top) / rect.height <= 0.5 ? 'before' : 'after'

    const parentMap = buildParentMap(tree)
    const projection = getProjection(
      tree,
      expanded,
      parentMap,
      draggedId,
      node.id,
      position,
      event.clientX - dragStartXRef.current
    )

    if (!projection) {
      return
    }

    projectionRef.current = projection

    if (dragOver.id === node.id && dragOver.position === position && dragOver.depth === projection.depth) {
      return
    }

    setDragOver({ id: node.id, position, depth: projection.depth })
  }

  const handleDragLeave = (): void => setDragOver({ id: null, position: null, depth: 0 })

  // Trailing drop zone: append the dragged node at the root (top) level, whatever its current nesting.
  const handleRootZoneOver = (event: React.DragEvent): void => {
    event.preventDefault()
    if (draggedId === null) {
      return
    }
    projectionRef.current = { depth: 0, parentId: null, beforeId: null }
    if (dragOver.id !== null) {
      setDragOver({ id: null, position: null, depth: 0 })
    }
    if (!rootDropActive) {
      setRootDropActive(true)
    }
  }

  const handleRootZoneLeave = (): void => setRootDropActive(false)

  const handleRootZoneDrop = (event: React.DragEvent): void => {
    setRootDropActive(false)
    handleDrop(event)
  }

  // Reset all drag state when the gesture ends (including drops outside any target).
  const handleDragEnd = (): void => {
    setDraggedId(null)
    setDragOver({ id: null, position: null, depth: 0 })
    setRootDropActive(false)
    projectionRef.current = null
  }

  const handleDrop = (event: React.DragEvent): void => {
    event.preventDefault()

    const sourceId = draggedId
    const projection = projectionRef.current

    setDraggedId(null)
    setDragOver({ id: null, position: null, depth: 0 })
    projectionRef.current = null

    if (sourceId === null || !projection) {
      return
    }

    const sourceNode = findNode(tree, sourceId)
    if (!sourceNode) {
      return
    }

    const nextParent = projection.parentId != null ? findNode(tree, projection.parentId) : null

    // Guard against dropping into the moved subtree, and honour the host's drop rules.
    if (nextParent && isDescendant(sourceNode, nextParent.id)) {
      return
    }
    if (canDrop && !canDrop(sourceNode, nextParent)) {
      return
    }

    const [treeWithoutSource, removed] = removeNode(tree, sourceId)

    if (!removed) {
      return
    }

    const nextTree = insertUnderParent(treeWithoutSource, projection.parentId, projection.beforeId, removed)

    if (projection.parentId != null) {
      setExpanded((prev) => new Set(prev).add(projection.parentId as string | number))
    }

    setTree(nextTree)
    onMoveNode?.(nextTree)
  }

  const dnd: IDndState | undefined = isDndEnabled
    ? {
        draggedId,
        dragOver,
        canDrag: canDrag as (node: ITableTreeNode) => boolean,
        onDragStart: handleDragStart,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        onDragEnd: handleDragEnd
      }
    : undefined

  if (isEmpty(treeData)) {
    return isLoading ? null : <PlaceholderWrapper>{placeholder}</PlaceholderWrapper>
  }

  return (
    <TreeWrapper id={TREE_SCROLL_CONTAINER_ID} ref={wrapperRef}>
      <Table>
        <thead>
          <tr>
            <HeadCell>DESCRIPTION</HeadCell>
            {columns.map((column) => (
              <HeadCell key={column.key}>
                <CellContent $width={column.width}>{column.header}</CellContent>
              </HeadCell>
            ))}
            {renderRowActions && <ActionsHeadCell>{actionsHeader}</ActionsHeadCell>}
          </tr>
        </thead>
        <tbody>
          {tree.map((node) => (
            <RowComponent
              dnd={dnd}
              key={node.id}
              node={node}
              depth={0}
              columns={columns}
              expanded={expanded}
              onRowClick={onRowClick}
              formWidth={viewportWidth}
              isRowDimmed={isRowDimmed}
              toggleExpand={toggleExpand}
              getNodeTitle={getNodeTitle}
              RowComponent={RowComponent}
              forceExpandAll={forceExpandAll}
              renderRowActions={renderRowActions}
              renderCreateForm={renderCreateForm}
            />
          ))}
        </tbody>
      </Table>
      {dnd && draggedId !== null && (
        <RootDropZone onDragOver={handleRootZoneOver} onDragLeave={handleRootZoneLeave} onDrop={handleRootZoneDrop}>
          {rootDropActive && <DropLine $depth={0} $position='before' />}
        </RootDropZone>
      )}
      {footer}
    </TreeWrapper>
  )
}

export default TableTree
